import {
  type Control,
  FieldPath,
  FieldValues,
  type FormState,
  type UseFormSetError,
  type UseFormSetValue,
  type UseFormWatch,
} from 'react-hook-form';
import { EnterpriseStructure, OwnershipType } from '@brighte/validation';
import { Text } from '@spark-web/text';
import { useApplicationData } from '../../provider';
import { LABELS, OWNERSHIP_DETAILS } from '../../utils';
import BeneficialIdentification from './BeneficialIdentification';
import BeneficialOwner from './BeneficialOwner';
import CorporateEntitiesField from './CorporateEntitiesField';
import Director from './Director';
import OwnershipTypeField from './OwnershipType';
import SmeDirector from './SmeDirector';
import SoleTraderIndividual from './SoleTraderIndividual';

type OwnershipDetailFieldsProps<TFieldValues extends FieldValues> = {
  businessType: EnterpriseStructure;
  control: Control<TFieldValues>;
  watch: UseFormWatch<TFieldValues>;
  setValue: UseFormSetValue<TFieldValues>;
  formState: FormState<TFieldValues>;
  setError: UseFormSetError<TFieldValues>;
};

function OwnershipDetailFields<TFieldValues extends FieldValues = FieldValues>({
  businessType,
  control,
  watch,
  setValue,
  formState,
  setError,
}: Readonly<OwnershipDetailFieldsProps<TFieldValues>>) {
  const { DESCRIPTION, DESCRIPTION_COMPANY } = OWNERSHIP_DETAILS;
  const { CHAIRMAN_ID, SECRETARY_ID, TREASURER_ID } = LABELS;

  const { OwnershipDetails: ownershipDetails, quote } = useApplicationData(['OwnershipDetails', 'quote']);
  const financialProductId = quote?.financialProduct?.id;

  const props = {
    watch,
    control,
    businessType,
    setValue,
    formState,
    setError,
  };

  // --- ACT SFA: Company ---
  if (businessType === EnterpriseStructure.Company) {
    const ownershipType = watch('enterprise.company.ownershipType' as FieldPath<TFieldValues>);
    const isProprietary = ownershipType === OwnershipType.Proprietary;

    return (
      <>
        <Text>{DESCRIPTION_COMPANY}</Text>
        <OwnershipTypeField
          name={'enterprise.company.ownershipType' as FieldPath<TFieldValues>}
          control={control}
          setValue={setValue}
        />

        {isProprietary && (
          <>
            <Director name={'enterprise.company.directors' as FieldPath<TFieldValues>} {...props} />
            <BeneficialOwner
              name="enterprise.company.beneficialOwners"
              defaultValues={ownershipDetails?.enterprise['company']?.beneficialOwners ?? []}
              financialProductId={financialProductId}
              {...props}
            />
          </>
        )}
      </>
    );
  }

  if (
    businessType === EnterpriseStructure.IncorporatedAssociation ||
    businessType === EnterpriseStructure.UnincorporatedAssociation
  ) {
    const businessTypeName =
      businessType === EnterpriseStructure.IncorporatedAssociation
        ? 'incorporatedAssociation'
        : 'unincorporatedAssociation';

    const roles = [
      { role: 'chairman', title: CHAIRMAN_ID },
      { role: 'secretary', title: SECRETARY_ID },
      { role: 'treasurer', title: TREASURER_ID },
    ];

    return (
      <>
        <Text>{DESCRIPTION}</Text>
        {roles.map(({ role, title }) => (
          <BeneficialIdentification
            key={role}
            name={`enterprise.${businessTypeName}.${role}[0].`}
            title={title}
            defaultValues={ownershipDetails?.enterprise[businessTypeName]?.[role][0] ?? {}}
            financialProductId={financialProductId}
            {...props}
          />
        ))}
      </>
    );
  }

  if (businessType === EnterpriseStructure.Partnership || businessType === EnterpriseStructure.Trust) {
    const entityKey = businessType === EnterpriseStructure.Partnership ? 'partnership' : 'trust';
    const ownershipType = watch(`enterprise.${entityKey}.ownershipType` as FieldPath<TFieldValues>);
    const isCorporate = ownershipType === OwnershipType.Corporate;

    return (
      <>
        <Text>{DESCRIPTION_COMPANY}</Text>
        <OwnershipTypeField
          name={`enterprise.${entityKey}.ownershipType` as FieldPath<TFieldValues>}
          control={control}
          setValue={setValue}
          options={OWNERSHIP_DETAILS.SME_OWNERSHIP_TYPE_OPTIONS}
          description={businessType === EnterpriseStructure.Trust ? OWNERSHIP_DETAILS.SME_TRUST_OWNERSHIP_TYPE_DESCRIPTION : OWNERSHIP_DETAILS.SME_OWNERSHIP_TYPE_DESCRIPTION}
        />

        {isCorporate && (
          <CorporateEntitiesField
            name={`enterprise.${entityKey}.corporateEntities` as FieldPath<TFieldValues>}
            control={control}
          />
        )}
        <SmeDirector<TFieldValues>
          name={`enterprise.${entityKey}.directors` as FieldPath<TFieldValues>}
          dropdownName={`${entityKey}.numOfDirectors` as FieldPath<TFieldValues>}
          showLicence={!isCorporate}
          showDirectors={!!ownershipType}
          roleLabel={businessType === EnterpriseStructure.Trust ? 'Trustee' : 'Partner'}
          {...props}
        />
      </>
    );
  }

  if (businessType === EnterpriseStructure.ProprietaryLimitedCompany) {
    return (
      <>
        <Text>{DESCRIPTION_COMPANY}</Text>
        <SmeDirector<TFieldValues>
          name={'enterprise.proprietaryLimitedCompany.directors' as FieldPath<TFieldValues>}
          dropdownName={'proprietaryLimitedCompany.numOfDirectors' as FieldPath<TFieldValues>}
          showLicence={false}
          {...props}
        />
      </>
    );
  }

  if (businessType === EnterpriseStructure.SoleTrader) {
    const soleTraderAddress =
      ownershipDetails?.enterprise?.structure === EnterpriseStructure.SoleTrader
        ? (ownershipDetails.enterprise.soleTrader?.individual?.address ?? null)
        : null;

    return (
      <SoleTraderIndividual
        name={'enterprise.soleTrader.individual' as FieldPath<TFieldValues>}
        control={control}
        watch={watch}
        setValue={setValue}
        defaultValues={{
          address: soleTraderAddress,
        }}
      />
    );
  }

  if (businessType === EnterpriseStructure.Other) {
    const otherAddress =
      ownershipDetails?.enterprise?.structure === EnterpriseStructure.Other
        ? (ownershipDetails.enterprise.other?.individual?.address ?? null)
        : null;

    return (
      <SoleTraderIndividual
        name={'enterprise.other.individual' as FieldPath<TFieldValues>}
        control={control}
        watch={watch}
        setValue={setValue}
        defaultValues={{
          address: otherAddress,
        }}
      />
    );
  }

  return null;
}

export default OwnershipDetailFields;
